import KpiChart from "@app/components/kpi-chart";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import { dateRangeFromISO, getDateRange } from "@app/lib/date-range";
import { getPerspectiveId } from "@app/lib/perspective";
import { getAuthToken } from "@app/relay/fetch-relay";
import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { Suspense, useEffect, useRef, useState, useTransition } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";
import * as ContextMenu from "@radix-ui/react-context-menu";

import type { transactionsPage_Query } from "@app/queries/transactionsPage_Query.graphql";
import type { transactionsPage_AttributeTxnsMutation } from "@app/queries/transactionsPage_AttributeTxnsMutation.graphql";
import type { transactionsPage_CreateRuleMutation } from "@app/queries/transactionsPage_CreateRuleMutation.graphql";
import type {
  transactionsPage_ClassifyTxnsMutation,
  TxnClassificationEnum,
} from "@app/queries/transactionsPage_ClassifyTxnsMutation.graphql";
import type { transactionsPage_SetTxnsNeedsReviewMutation } from "@app/queries/transactionsPage_SetTxnsNeedsReviewMutation.graphql";
import type { transactionsPage_AssignTxnNatureMutation } from "@app/queries/transactionsPage_AssignTxnNatureMutation.graphql";

import StakeholderTxnsDialog from "@app/pages/categorize/stakeholder-txns-dialog";
import { useToast } from "@app/components/toast";
import { ProvenanceBadge } from "./provenance-badge";
import AssignCell from "./assign-cell";
import CategoryCell from "./category-cell";
import RuleSuggestionDialog from "./rule-suggestion-dialog";
import CreateRuleDialog from "./components/create-rule-dialog";
import { ruleFilterInput } from "@app/components/rule-account-filter";
import type { RuleAccountFilterValue } from "@app/components/rule-account-filter";

export const transactionsQuery = graphql`
  query transactionsPage_Query(
    $month: String
    $from: String
    $accountNumber: String
    $flow: TxnFlowEnum
    $attributed: Boolean
    $categorized: Boolean
    $perspectiveId: ID
    $counterpartyId: ID
    $natureId: ID
    $pnlLine: PnlLineDrillEnum
    $query: String
    $sort: TxnSortEnum
    $sortDirection: String
    $dailyHealth: Boolean
    $excludeNeedsReview: Boolean
    $limit: Int
    $offset: Int
  ) {
    transactions(
      month: $month
      from: $from
      accountNumber: $accountNumber
      flow: $flow
      attributed: $attributed
      categorized: $categorized
      perspectiveId: $perspectiveId
      counterpartyId: $counterpartyId
      natureId: $natureId
      pnlLine: $pnlLine
      query: $query
      sort: $sort
      sortDirection: $sortDirection
      dailyHealth: $dailyHealth
      excludeNeedsReview: $excludeNeedsReview
      limit: $limit
      offset: $offset
    ) {
      totalCount
      limit
      months
      accounts {
        number
        nickname
      }
      health {
        period
        categorized
        uncategorized
        internal
      }
      txns {
        txn {
          id
        }
        postingDate
        description
        amount
        kind
        accountNumber
        otherBankAccount {
          nickname
          last4
        }
        internal
        classification
        expenseNature {
          id
          name
        }
        counterparty {
          id
          name
        }
        categoryNode {
          id
          name
        }
        attribution {
          source
          performedAt
          user {
            email
          }
          rule {
            regex
          }
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
      number
    }
    categories(perspectiveId: $perspectiveId) {
      id
      name
      parent {
        id
      }
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

const attributeMutation = graphql`
  mutation transactionsPage_AttributeTxnsMutation(
    $input: AttributeTxnsInputObject!
  ) {
    attributeTxns(input: $input) {
      stakeholder {
        id
        name
      }
      suggestion {
        regex
        matchKind
        additionalCount
        totalCount
      }
      errors {
        message
      }
    }
  }
`;

const createRuleMutation = graphql`
  mutation transactionsPage_CreateRuleMutation($input: CreateRuleInputObject!) {
    createRule(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const assignNatureMutation = graphql`
  mutation transactionsPage_AssignTxnNatureMutation(
    $input: AssignTxnNatureInputObject!
  ) {
    assignTxnNature(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const classifyMutation = graphql`
  mutation transactionsPage_ClassifyTxnsMutation(
    $input: ClassifyTxnsInputObject!
  ) {
    classifyTxns(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const setNeedsReviewMutation = graphql`
  mutation transactionsPage_SetTxnsNeedsReviewMutation(
    $input: SetTxnsNeedsReviewInputObject!
  ) {
    setTxnsNeedsReview(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

// Labels + badge colors for each classification. null = clear.
const CLASSIFICATIONS: { value: TxnClassificationEnum; label: string }[] = [
  { value: "FRAUDULENT", label: "Fraudulent" },
  { value: "LOAN_PROCEEDS", label: "Loan proceeds" },
  { value: "LOAN_REPAYMENT", label: "Loan repayment" },
  { value: "DIVIDEND", label: "Dividend payment" },
  { value: "TAX_PAYMENT", label: "Tax payment" },
  { value: "TAX_REFUND", label: "Tax payback" },
  { value: "SALARY", label: "Salary / wages" },
];

// Record<string, …> rather than Record<TxnClassificationEnum, …>: Relay enum
// types carry a synthetic "%future added value" member we don't want to map.
const CLASSIFICATION_BADGE: Record<string, string> = {
  FRAUDULENT: "bg-red-100 text-red-700",
  LOAN_PROCEEDS: "bg-violet-100 text-violet-700",
  LOAN_REPAYMENT: "bg-violet-100 text-violet-700",
  DIVIDEND: "bg-amber-100 text-amber-800",
  TAX_PAYMENT: "bg-purple-100 text-purple-700",
  TAX_REFUND: "bg-purple-100 text-purple-700",
  SALARY: "bg-slate-100 text-slate-600",
};

const classificationLabel = (value: TxnClassificationEnum) =>
  CLASSIFICATIONS.find((c) => c.value === value)?.label ?? value;

// "Show everything like this" (query-by-example): the merchant stem — the description
// up to the first long digit run (a card#/date/transaction-id), which the description
// filter then matches. Falls back to the full description when there's no such stem.
const likeThisQuery = (description: string) => {
  const stem = description.split(/\s+\d{3,}/)[0].trim();
  return stem.length >= 3 ? stem : description.trim();
};

export type TxnSort =
  | "DATE"
  | "AMOUNT"
  | "DESCRIPTION"
  | "COUNTERPARTY"
  | "CATEGORY"
  | "NATURE"
  | "ACCOUNT";

export type TransactionsSearch = {
  month?: string;
  account?: string;
  flow?: "COST" | "REVENUE" | "INTERNAL";
  attributed?: boolean;
  categorized?: boolean;
  counterparty?: string;
  nature?: string;
  pnlLine?:
    | "REVENUE"
    | "COGS"
    | "S_AND_M"
    | "R_AND_D"
    | "G_AND_A"
    | "UNCATEGORIZED"
    | "TAXES";
  query?: string;
  sort?: TxnSort;
  sortDirection?: "ASC" | "DESC";
  offset?: number;
};

// Txn rows are lighter than the stakeholder rows and users scan more of them → 100.
export const PAGE_SIZE = 100;

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

// Flow flavors that override the plain cost/revenue read of the amount's sign.
// Tax, tax payback and owner dividends are real cash but NOT operating money, so
// the flow dot has to name them — otherwise a tax payment is indistinguishable
// from a supplier cost, and a tax rebate from revenue.
const FLOW_FLAVOR: Record<string, readonly [string, string]> = {
  TAX_PAYMENT: ["tax", "bg-purple-600"],
  TAX_REFUND: ["tax payback", "bg-purple-300"],
  DIVIDEND: ["dividend payment", "bg-amber-500"],
};

function FlowPill({
  internal,
  amount,
  classification,
}: {
  internal: boolean;
  amount: number;
  classification?: string | null;
}) {
  const flavor = classification ? FLOW_FLAVOR[classification] : undefined;
  const [label, color] =
    flavor ??
    (internal
      ? ["internal", "bg-blue-500"]
      : amount > 0
      ? ["revenue", "bg-emerald-500"]
      : ["cost", "bg-orange-500"]);

  return (
    <span title={label} className={`shrink-0 w-2 h-2 rounded-full ${color}`} />
  );
}

export default function TransactionsPage({
  queryRef,
  search,
  onSearchChange,
  onPageChange,
  monthOverride,
}: {
  queryRef: PreloadedQuery<transactionsPage_Query>;
  search: TransactionsSearch;
  onSearchChange: (next: TransactionsSearch) => void;
  onPageChange: (offset: number) => void;
  monthOverride?: string;
}) {
  // The route loader mints a fresh queryRef on every filter/search/month change.
  // Reading it directly suspends OUTSIDE a transition, so React tears the page
  // down for a fallback — that is the "navigating wipes the page" flash. Adopting
  // the new ref through a transition instead keeps the current rows committed
  // while the next ones load, and `refreshing` is what fades the table.
  const [refreshing, startRefresh] = useTransition();
  const [activeRef, setActiveRef] = useState(queryRef);
  useEffect(() => {
    if (queryRef === activeRef) return;
    startRefresh(() => setActiveRef(queryRef));
  }, [queryRef, activeRef]);

  // The header sits ABOVE the suspense boundary, so whatever suspends below it
  // (the first paint of a deep link, a hard refetch) only ever replaces what is
  // under the title — the month arrows stay on screen. They need the months the
  // data knows about, so the body reports them up once it has them.
  const [months, setMonths] = useState<readonly string[]>([]);
  const monthList = [...months].sort();
  const monthIndex = monthOverride ? monthList.indexOf(monthOverride) : -1;
  const prevMonth = monthIndex > 0 ? monthList[monthIndex - 1] : null;
  const nextMonth =
    monthIndex >= 0 && monthIndex < monthList.length - 1
      ? monthList[monthIndex + 1]
      : null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold">Transactions</h1>
        {monthOverride && (
          <span className="flex items-center gap-2 text-sm">
            <Link to="/transactions" className="text-gray-500 hover:underline">
              ← all
            </Link>
            {prevMonth ? (
              <Link
                to="/transactions/$month"
                params={{ month: prevMonth }}
                search={search}
                className="px-2 py-0.5 rounded border hover:bg-gray-50"
              >
                ◀
              </Link>
            ) : (
              <span className="px-2 py-0.5 rounded border text-gray-300">
                ◀
              </span>
            )}
            <span className="font-medium">{monthOverride}</span>
            {nextMonth ? (
              <Link
                to="/transactions/$month"
                params={{ month: nextMonth }}
                search={search}
                className="px-2 py-0.5 rounded border hover:bg-gray-50"
              >
                ▶
              </Link>
            ) : (
              <span className="px-2 py-0.5 rounded border text-gray-300">
                ▶
              </span>
            )}
          </span>
        )}
        {(() => {
          const active = search.flow === "COST" && search.attributed === false;
          return (
            <button
              type="button"
              onClick={() =>
                onSearchChange(
                  active
                    ? { ...search, flow: undefined, attributed: undefined }
                    : { ...search, flow: "COST", attributed: false }
                )
              }
              className={`text-xs px-2 py-1 rounded border ${
                active
                  ? "bg-amber-500 text-white border-amber-500"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {active
                ? "Showing uncategorized costs ✕"
                : "Show uncategorized costs"}
            </button>
          );
        })()}
      </div>

      <ErrorBoundary
        fallback={
          <div className="p-6 text-red-500">Error loading transactions</div>
        }
      >
        <Suspense
          fallback={
            <div className="p-6 text-gray-500">Loading transactions...</div>
          }
        >
          <TransactionsBody
            activeRef={activeRef}
            refreshing={refreshing}
            search={search}
            onSearchChange={onSearchChange}
            onPageChange={onPageChange}
            monthOverride={monthOverride}
            onMonths={setMonths}
          />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

function TransactionsBody({
  activeRef,
  refreshing,
  search,
  onSearchChange,
  onPageChange,
  monthOverride,
  onMonths,
}: {
  activeRef: PreloadedQuery<transactionsPage_Query>;
  refreshing: boolean;
  search: TransactionsSearch;
  onSearchChange: (next: TransactionsSearch) => void;
  onPageChange: (offset: number) => void;
  monthOverride?: string;
  onMonths: (months: readonly string[]) => void;
}) {
  const preloaded = usePreloadedQuery(transactionsQuery, activeRef);
  const notify = useToast();

  const [data, setData] = useState(preloaded);
  useEffect(() => setData(preloaded), [preloaded]);

  const [openAssign, setOpenAssign] = useState<string | null>(null);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  // U9: drill into one counterparty's full transactions dialog from this page.
  const [txnsDialogStakeholder, setTxnsDialogStakeholder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // The "Create rule…" dialog, scoped to one txn row.
  const [createRuleFor, setCreateRuleFor] = useState<{
    txnId: string;
    description: string;
  } | null>(null);

  // Bulk selection (shift-click selects a range from the last-clicked row).
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const lastIndexRef = useRef<number | null>(null);
  const shiftHeldRef = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Shift") shiftHeldRef.current = e.type === "keydown";
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  // The bulk-attribute popover (combobox) and the rule-suggestion dialog.
  const [attributeTargetIds, setAttributeTargetIds] = useState<
    readonly string[] | null
  >(null);
  // Attributing a selection is slow (it re-derives downstream), so the combobox
  // row that was clicked has to show that the request is in flight.
  const [attributing, setAttributing] = useState(false);
  const [ruleState, setRuleState] = useState<{
    stakeholderId: string;
    stakeholderName: string;
    prefillSourceIds: string[];
    suggestion: {
      regex: string;
      matchKind: string;
      additionalCount: number;
      totalCount: number;
    };
  } | null>(null);
  const [savingRule, setSavingRule] = useState(false);

  // The categorization-health chart hides internal transfers by default — they
  // dwarf real costs and aren't the thing you're triaging here. Opt back in
  // with the "Show internal" checkbox above the chart.
  const [showInternal, setShowInternal] = useState(false);

  // Debounce the free-text filter so we don't refetch on every keystroke.
  const [queryInput, setQueryInput] = useState(search.query ?? "");
  useEffect(() => setQueryInput(search.query ?? ""), [search.query]);
  useEffect(() => {
    const next = queryInput.trim() || undefined;
    if (next === search.query) return;
    const timer = setTimeout(
      () => onSearchChange({ ...search, query: next }),
      300
    );
    return () => clearTimeout(timer);
  }, [queryInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshInPlace = () => {
    // Refetch the EXACT operation the page is showing — the same variables the
    // route loader used. Rebuilding variables by hand drifted from the loader
    // (it dropped `from`), so the refetch wrote a different store entry while
    // the preloaded→data sync kept reverting in-place edits to the stale rows.
    // activeRef.variables can't drift from what's displayed.
    fetchQuery<transactionsPage_Query>(
      environment,
      transactionsQuery,
      activeRef.variables
    )
      .toPromise()
      .then((fresh) => fresh && setData(fresh));
  };

  const { totalCount, months, accounts, health, txns } = data.transactions;
  const categories = data.categories;

  // Offset pagination lives in the URL search (offset), so a paged view is shareable
  // and the route loader re-fetches it. Any filter/sort change resets to page 0 (the
  // route wrapper forces offset:0 on onSearchChange); Prev/Next use onPageChange.
  const page = Math.floor((search.offset ?? 0) / PAGE_SIZE);
  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalCount);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < totalCount;

  // Hand the header (above the suspense boundary) the months its arrows step
  // through. `months` is the response's own array, so this fires per load.
  useEffect(() => onMonths(months), [months, onMonths]);

  const allStakeholders = data.allStakeholders.filter(
    (s) =>
      !s.name.startsWith("&") &&
      !s.name.toLowerCase().includes("root stakeholder") &&
      s.name !== "_root"
  );
  const accountOptions = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  // Export the COMPLETE filtered set (not just the loaded page) from the server
  // endpoint — the same filters the list shows, mapped to its snake_case params. This
  // is why pagination is safe for export: the download is never limited to the page.
  const [exporting, setExporting] = useState(false);
  const exportXls = async () => {
    const p: Record<string, string> = { exclude_needs_review: "true" };
    const month = monthOverride ?? search.month;
    if (month) p.month = month;
    const from = dateRangeFromISO(getDateRange());
    if (from) p.from = from;
    if (search.account) p.account_number = search.account;
    if (search.flow) p.flow = search.flow;
    if (search.attributed !== undefined) p.attributed = String(search.attributed);
    if (search.categorized !== undefined) p.categorized = String(search.categorized);
    if (search.counterparty) p.counterparty_id = search.counterparty;
    if (search.nature) p.nature_id = search.nature;
    if (search.pnlLine) p.pnl_line = search.pnlLine;
    if (search.query) p.query = search.query;
    if (search.sort) p.sort = search.sort;
    if (search.sortDirection) p.sort_direction = search.sortDirection;
    const perspectiveId = getPerspectiveId();
    if (perspectiveId) p.perspective_id = perspectiveId;

    setExporting(true);
    try {
      const qs = new URLSearchParams(p).toString();
      const response = await fetch(`/exports/transactions?${qs}`, {
        headers: { Authorization: `Bearer ${getAuthToken() ?? ""}` },
      });
      if (!response.ok) {
        notify({ message: "Export failed. Please try again.", kind: "error" });
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `transactions-${new Date().toISOString().slice(0, 10)}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      notify({ message: "Export failed. Please try again.", kind: "error" });
    } finally {
      setExporting(false);
    }
  };

  // While the new rows are in flight the current ones are stale: dim them and stop
  // taking clicks on them, so nobody edits a row that is about to be replaced.
  const fadeWhileRefreshing = `transition-opacity duration-150 ${
    refreshing ? "opacity-40 pointer-events-none" : "opacity-100"
  }`;

  const totalCategorized = health.reduce((sum, h) => sum + h.categorized, 0);
  const totalCosts =
    totalCategorized + health.reduce((sum, h) => sum + h.uncategorized, 0);
  const overallShare =
    totalCosts > 0 ? (100 * totalCategorized) / totalCosts : 0;

  const healthSeries = [
    {
      name: "Categorized",
      type: "bar" as const,
      stack: "health",
      color: "#0f766e",
      points: health.map((h) => ({ month: h.period, value: h.categorized })),
    },
    {
      name: "Uncategorized",
      type: "bar" as const,
      stack: "health",
      color: "#f59e0b",
      points: health.map((h) => ({ month: h.period, value: h.uncategorized })),
    },
    ...(showInternal
      ? [
          {
            name: "Internal transfers",
            type: "bar" as const,
            stack: "health",
            color: "#3b82f6",
            points: health.map((h) => ({
              month: h.period,
              value: h.internal,
            })),
          },
        ]
      : []),
  ];

  const toggleRow = (txnId: string, index: number) => {
    // Capture the anchor/shift now: the ref is reassigned below, synchronously,
    // before React runs the async setSelected updater.
    const anchor = lastIndexRef.current;
    const extendRange = shiftHeldRef.current && anchor !== null;
    setSelected((prev) => {
      const next = new Set(prev);
      if (extendRange) {
        const [from, to] = [anchor as number, index].sort((a, b) => a - b);
        for (let i = from; i <= to; i++) next.add(txns[i].txn.id);
      } else if (next.has(txnId)) {
        next.delete(txnId);
      } else {
        next.add(txnId);
      }
      return next;
    });
    lastIndexRef.current = index;
  };

  const sortBy = (column: TxnSort) => {
    const flip =
      search.sort === column && search.sortDirection !== "ASC"
        ? "ASC"
        : search.sort === column
        ? "DESC"
        : "DESC";
    onSearchChange({
      ...search,
      sort: column,
      sortDirection: column === search.sort ? flip : "DESC",
    });
  };

  const sortArrow = (column: TxnSort) =>
    search.sort === column
      ? search.sortDirection === "ASC"
        ? " ↑"
        : " ↓"
      : "";

  const attribute = (
    txnIds: readonly string[],
    choice: { stakeholderId?: string; name?: string }
  ) => {
    setAttributing(true);
    commitMutation<transactionsPage_AttributeTxnsMutation>(environment, {
      mutation: attributeMutation,
      variables: {
        input: {
          txnIds: [...txnIds],
          ...choice,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: (response) => {
        const payload = response.attributeTxns;
        setAttributing(false);
        setAttributeTargetIds(null);
        setSelected(new Set());
        refreshInPlace();
        const stakeholder = payload?.stakeholder;
        const suggestion = payload?.suggestion;
        if (stakeholder && suggestion) {
          const numbers = new Set(
            txns
              .filter((r) => txnIds.includes(r.txn.id))
              .map((r) => r.accountNumber)
              .filter(Boolean)
          );
          setRuleState({
            stakeholderId: stakeholder.id,
            stakeholderName: stakeholder.name,
            prefillSourceIds: data.bankAccounts
              .filter((a) => numbers.has(a.number))
              .map((a) => a.id),
            suggestion,
          });
        }
      },
      onError: (err) => {
        setAttributing(false);
        window.alert(err.message);
      },
    });
  };

  const classify = (
    txnIds: readonly string[],
    classification: TxnClassificationEnum | null
  ) => {
    commitMutation<transactionsPage_ClassifyTxnsMutation>(environment, {
      mutation: classifyMutation,
      variables: { input: { txnIds: [...txnIds], classification } },
      onCompleted: () => {
        setSelected(new Set());
        refreshInPlace();
      },
      onError: (err) => window.alert(err.message),
    });
  };

  // Bulk-set (or clear) the expense nature on the selection (the by-nature axis).
  // A null expenseNatureId clears the tag. Manual wins over rule-derived (R5).
  const assignNature = (txnIds: readonly string[], expenseNatureId: string | null) => {
    commitMutation<transactionsPage_AssignTxnNatureMutation>(environment, {
      mutation: assignNatureMutation,
      variables: { input: { txnIds: [...txnIds], expenseNatureId } },
      onCompleted: () => {
        setSelected(new Set());
        refreshInPlace();
      },
      onError: (err) => window.alert(err.message),
    });
  };

  // Send the selection to the needs-review worklist — the manual op_needs_review
  // override. The flagged txns leave the operating corpus and surface on /needs-review,
  // where their internal counterparty account is assigned. Mirrors the 90×90 dialog's
  // "Send to worklist" action.
  const sendToNeedsReview = (txnIds: readonly string[]) => {
    const count = txnIds.length;
    commitMutation<transactionsPage_SetTxnsNeedsReviewMutation>(environment, {
      mutation: setNeedsReviewMutation,
      variables: { input: { txnIds: [...txnIds], needsReview: true } },
      onCompleted: (response) => {
        const errors = response.setTxnsNeedsReview?.errors;
        if (errors?.length) {
          notify({ message: errors[0].message, kind: "error" });
          return;
        }
        setSelected(new Set());
        refreshInPlace();
        notify({
          message: `Sent ${count} to the needs-review worklist.`,
          kind: "success",
        });
      },
      onError: () =>
        notify({ message: "Send to needs-review failed — please try again.", kind: "error" }),
    });
  };

  const confirmRule = (
    filter: RuleAccountFilterValue,
    regex: string,
    matchKind: string
  ) => {
    if (!ruleState) return;
    setSavingRule(true);
    commitMutation<transactionsPage_CreateRuleMutation>(environment, {
      mutation: createRuleMutation,
      variables: {
        input: {
          stakeholderId: ruleState.stakeholderId,
          ...ruleFilterInput(filter),
          regex,
          matchKind,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: () => {
        setSavingRule(false);
        setRuleState(null);
        refreshInPlace();
      },
      onError: (err) => {
        setSavingRule(false);
        window.alert(err.message);
      },
    });
  };

  return (
    <>
      <div className="flex items-baseline justify-between">
        <p className="text-sm text-gray-500">
          <span className="font-semibold text-gray-900">
            {overallShare.toFixed(1)}%
          </span>{" "}
          of all-time costs categorized — manage{" "}
          <a href="/rules" className="underline">
            rules
          </a>{" "}
          or work the{" "}
          <a href="/review" className="underline">
            review queue
          </a>
        </p>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showInternal}
            onChange={(e) => setShowInternal(e.target.checked)}
            className="rounded border-gray-300"
          />
          Show internal
        </label>
      </div>

      <KpiChart
        // Remount on toggle: echarts-for-react merges options, so dropping the
        // internal series from the array alone won't clear the rendered bars.
        key={showInternal ? "health-with-internal" : "health-no-internal"}
        title={
          showInternal
            ? "Categorization health — real costs (categorized vs not) and internal transfers in blue"
            : "Categorization health — real costs (categorized vs not)"
        }
        series={healthSeries}
        format="money"
        onMonthClick={
          monthOverride
            ? () => {}
            : (clicked) => onSearchChange({ ...search, month: clicked })
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <span className="relative inline-flex items-center">
          <input
            type="search"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Filter by description…"
            className="rounded border px-2 py-1.5 pr-7 text-sm w-56"
          />
          {refreshing && (
            <span className="absolute right-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          )}
        </span>

        <select
          value={search.account ?? ""}
          onChange={(e) =>
            onSearchChange({ ...search, account: e.target.value || undefined })
          }
          className="rounded border px-2 py-1.5 text-sm bg-white"
        >
          <option value="">All accounts</option>
          {accounts.map((account) => (
            <option key={account.number} value={account.number}>
              {account.nickname ?? account.number}
            </option>
          ))}
        </select>

        <select
          value={search.counterparty ?? ""}
          onChange={(e) =>
            onSearchChange({
              ...search,
              counterparty: e.target.value || undefined,
            })
          }
          className="rounded border px-2 py-1.5 text-sm bg-white max-w-[12rem]"
          title="Show one counterparty's transactions (for salary vs dividend review)"
        >
          <option value="">All counterparties</option>
          {allStakeholders.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        {!monthOverride && (
          <select
            value={search.month ?? ""}
            onChange={(e) =>
              onSearchChange({ ...search, month: e.target.value || undefined })
            }
            className="rounded border px-2 py-1.5 text-sm bg-white"
          >
            <option value="">All months</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}

        <select
          value={search.flow ?? ""}
          onChange={(e) =>
            onSearchChange({
              ...search,
              flow: (e.target.value || undefined) as TransactionsSearch["flow"],
            })
          }
          className="rounded border px-2 py-1.5 text-sm bg-white"
        >
          <option value="">All flows</option>
          <option value="COST">Costs</option>
          <option value="REVENUE">Revenue</option>
          <option value="INTERNAL">Internal</option>
        </select>

        <select
          value={
            search.attributed === undefined ? "" : String(search.attributed)
          }
          onChange={(e) =>
            onSearchChange({
              ...search,
              attributed:
                e.target.value === "" ? undefined : e.target.value === "true",
            })
          }
          className="rounded border px-2 py-1.5 text-sm bg-white"
        >
          <option value="">Attributed & not</option>
          <option value="true">Attributed</option>
          <option value="false">Unattributed</option>
        </select>

        <select
          value={
            search.categorized === undefined ? "" : String(search.categorized)
          }
          onChange={(e) =>
            onSearchChange({
              ...search,
              categorized:
                e.target.value === "" ? undefined : e.target.value === "true",
            })
          }
          className="rounded border px-2 py-1.5 text-sm bg-white"
        >
          <option value="">Categorized & not</option>
          <option value="true">Categorized</option>
          <option value="false">Uncategorized</option>
        </select>

        <select
          value={search.nature ?? ""}
          onChange={(e) =>
            onSearchChange({ ...search, nature: e.target.value || undefined })
          }
          className="rounded border px-2 py-1.5 text-sm bg-white max-w-[12rem]"
          title="Show one expense nature (the by-nature axis)"
        >
          <option value="">All natures</option>
          {data.expenseNatures.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </select>

        <p className="text-xs text-gray-400 ml-2">
          {totalCount.toLocaleString()} transactions
          {totalCount > PAGE_SIZE &&
            ` — showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()}`}
        </p>
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <button
              type="button"
              disabled={!hasPrev}
              onClick={() => onPageChange(Math.max(0, (page - 1) * PAGE_SIZE))}
              className="px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-30"
            >
              ← Prev
            </button>
            <span>
              Page {page + 1} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
            </span>
            <button
              type="button"
              disabled={!hasNext}
              onClick={() => onPageChange((page + 1) * PAGE_SIZE)}
              className="px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-30"
            >
              Next →
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={exportXls}
          disabled={exporting || totalCount === 0}
          title="Download every transaction in this view as an Excel (.xlsx) file"
          className="ml-auto px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 text-sm whitespace-nowrap"
        >
          {exporting ? "Exporting…" : "Export to Excel"}
        </button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <span>{selected.size} selected</span>
          <button
            type="button"
            onClick={() => setAttributeTargetIds([...selected])}
            className="px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 text-xs"
          >
            Attribute to…
          </button>
          <select
            value=""
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") return;
              classify(
                [...selected],
                value === "CLEAR" ? null : (value as TxnClassificationEnum)
              );
            }}
            className="rounded border border-amber-300 px-2 py-1 text-xs bg-white text-amber-900"
          >
            <option value="">Classify as…</option>
            {CLASSIFICATIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
            <option value="CLEAR">Clear classification</option>
          </select>
          <select
            value=""
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") return;
              assignNature([...selected], value === "CLEAR" ? null : value);
            }}
            className="rounded border border-amber-300 px-2 py-1 text-xs bg-white text-amber-900"
          >
            <option value="">Tag nature…</option>
            {data.expenseNatures.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
            <option value="CLEAR">Clear nature</option>
          </select>
          <button
            type="button"
            onClick={() => sendToNeedsReview([...selected])}
            className="px-2 py-1 rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 text-xs"
          >
            Send to needs-review
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs underline hover:no-underline"
          >
            Clear
          </button>
          <span className="text-xs text-amber-700">
            tip: shift-click to select a range, or right-click a row
          </span>
        </div>
      )}

      <div className="relative bg-white rounded border overflow-x-auto">
        {refreshing && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center pt-6">
            <span className="flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-xs text-gray-600 shadow">
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              Updating transactions…
            </span>
          </div>
        )}
        <table className={`w-full text-sm ${fadeWhileRefreshing}`}>
          <thead>
            <tr className="text-left text-xs text-gray-400 uppercase border-b">
              <th className="px-3 py-2 w-8"></th>
              <th
                className="px-3 py-2 cursor-pointer select-none hover:text-gray-600"
                onClick={() => sortBy("DATE")}
              >
                Date{sortArrow("DATE")}
              </th>
              <th
                className="px-3 py-2 cursor-pointer select-none hover:text-gray-600"
                onClick={() => sortBy("ACCOUNT")}
              >
                Account{sortArrow("ACCOUNT")}
              </th>
              <th
                className="px-3 py-2 cursor-pointer select-none hover:text-gray-600"
                onClick={() => sortBy("DESCRIPTION")}
              >
                Description{sortArrow("DESCRIPTION")}
              </th>
              <th
                className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-600"
                onClick={() => sortBy("AMOUNT")}
              >
                Amount{sortArrow("AMOUNT")}
              </th>
              <th
                className="px-3 py-2 cursor-pointer select-none hover:text-gray-600"
                onClick={() => sortBy("CATEGORY")}
              >
                Category{sortArrow("CATEGORY")}
              </th>
              <th
                className="px-3 py-2 cursor-pointer select-none hover:text-gray-600"
                onClick={() => sortBy("COUNTERPARTY")}
              >
                Counterparty{sortArrow("COUNTERPARTY")}
              </th>
              <th
                className="px-3 py-2 cursor-pointer select-none hover:text-gray-600"
                onClick={() => sortBy("NATURE")}
              >
                Nature{sortArrow("NATURE")}
              </th>
            </tr>
          </thead>
          <tbody>
            {txns.map((txn, index) => {
              const id = txn.txn.id;
              const isSelected = selected.has(id);
              const targetCount = isSelected ? selected.size : 1;
              return (
                <ContextMenu.Root key={id}>
                  <ContextMenu.Trigger
                    asChild
                    onContextMenu={() => {
                      if (!selected.has(id)) {
                        setSelected(new Set([id]));
                        lastIndexRef.current = index;
                      }
                    }}
                  >
                    <tr
                      className={`border-b last:border-b-0 ${
                        isSelected ? "bg-amber-50" : ""
                      }`}
                    >
                      <td className="px-3 py-1.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(id, index)}
                        />
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                        {txn.postingDate}
                      </td>
                      <td className="px-3 py-1.5 text-gray-500">
                        {txn.accountNumber}
                      </td>
                      <td
                        className="px-3 py-1.5 max-w-md"
                        title={`${txn.kind} — ${txn.description}`}
                      >
                        <span className="flex items-start gap-1.5">
                          {txn.classification && (
                            <span
                              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                                CLASSIFICATION_BADGE[txn.classification] ??
                                "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {classificationLabel(txn.classification)}
                            </span>
                          )}
                          <span className="min-w-0 line-clamp-2 break-words">
                            {txn.description}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              onSearchChange({ ...search, query: likeThisQuery(txn.description) })
                            }
                            title="Show everything like this"
                            aria-label="Show everything like this"
                            className="shrink-0 text-gray-300 hover:text-emerald-600"
                          >
                            ≈
                          </button>
                        </span>
                        {txn.internal && (
                          <span className="block text-[11px] text-gray-400 mt-0.5">
                            {(() => {
                              const dest = txn.otherBankAccount
                                ? txn.otherBankAccount.nickname ||
                                  `••${txn.otherBankAccount.last4 ?? ""}`
                                : null;
                              const here = txn.accountNumber ?? "—";
                              if (!dest) return "internal transfer";
                              return txn.amount > 0
                                ? `${dest} → ${here}`
                                : `${here} → ${dest}`;
                            })()}
                          </span>
                        )}
                        <span className="mt-0.5 block">
                          <ProvenanceBadge attribution={txn.attribution} />
                        </span>
                      </td>
                      <td
                        className={`px-3 py-1.5 text-right whitespace-nowrap ${
                          txn.internal
                            ? "text-gray-400"
                            : txn.amount < 0
                            ? "text-red-700"
                            : "text-green-700"
                        }`}
                      >
                        {money(txn.amount)}
                      </td>
                      <td className="px-3 py-1.5">
                        <CategoryCell
                          stakeholderId={txn.counterparty?.id ?? null}
                          categories={categories}
                          currentCategoryId={txn.categoryNode?.id ?? null}
                          open={openCategory === id}
                          onOpenChange={(next) =>
                            setOpenCategory(next ? id : null)
                          }
                          onAssigned={refreshInPlace}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="flex items-center gap-1.5">
                          <FlowPill
                            internal={txn.internal}
                            amount={txn.amount}
                            classification={txn.classification}
                          />
                          {txn.counterparty && openAssign !== id && (
                            <>
                              <Link
                                to="/stakeholders/$stakeholderId"
                                params={{ stakeholderId: txn.counterparty.id }}
                                className="hover:underline"
                              >
                                {txn.counterparty.name}
                              </Link>
                              <button
                                type="button"
                                onClick={() =>
                                  setTxnsDialogStakeholder(
                                    txn.counterparty
                                      ? {
                                          id: txn.counterparty.id,
                                          name: txn.counterparty.name,
                                        }
                                      : null
                                  )
                                }
                                title="Open the full transactions dialog"
                                className="text-gray-300 hover:text-gray-600"
                              >
                                ⤢
                              </button>
                            </>
                          )}
                          <AssignCell
                            txnId={id}
                            options={allStakeholders}
                            current={txn.counterparty ?? undefined}
                            open={openAssign === id}
                            onOpenChange={(next) =>
                              setOpenAssign(next ? id : null)
                            }
                            onAssigned={refreshInPlace}
                          />
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-indigo-700">
                        {txn.expenseNature?.name ?? (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  </ContextMenu.Trigger>
                  <ContextMenu.Portal>
                    <ContextMenu.Content className="min-w-44 bg-white rounded-md border shadow-lg py-1 text-sm z-50">
                      <ContextMenu.Item
                        onSelect={() =>
                          setAttributeTargetIds(
                            isSelected ? [...selected] : [id]
                          )
                        }
                        className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer outline-none"
                      >
                        Attribute {targetCount}{" "}
                        {targetCount === 1 ? "transaction" : "transactions"} to…
                      </ContextMenu.Item>
                      <ContextMenu.Item
                        onSelect={() =>
                          sendToNeedsReview(isSelected ? [...selected] : [id])
                        }
                        className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer outline-none"
                      >
                        Send {targetCount}{" "}
                        {targetCount === 1 ? "transaction" : "transactions"} to
                        needs-review
                      </ContextMenu.Item>
                      <ContextMenu.Item
                        onSelect={() =>
                          setCreateRuleFor({
                            txnId: id,
                            description: txn.description,
                          })
                        }
                        className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer outline-none"
                      >
                        Create rule…
                      </ContextMenu.Item>
                    </ContextMenu.Content>
                  </ContextMenu.Portal>
                </ContextMenu.Root>
              );
            })}
          </tbody>
        </table>
        {txns.length === 0 && (
          <p className={`p-6 text-sm text-gray-400 ${fadeWhileRefreshing}`}>
            No transactions match these filters.
          </p>
        )}
      </div>

      {attributeTargetIds && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
          onClick={() => !attributing && setAttributeTargetIds(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-sm font-semibold text-gray-800 mb-2">
              Attribute {attributeTargetIds.length}{" "}
              {attributeTargetIds.length === 1 ? "transaction" : "transactions"}{" "}
              to
            </h2>
            <StakeholderCombobox
              options={allStakeholders}
              busy={attributing}
              onPick={(choice) => attribute(attributeTargetIds, choice)}
            />
          </div>
        </div>
      )}

      {ruleState && (
        <RuleSuggestionDialog
          stakeholderName={ruleState.stakeholderName}
          suggestion={ruleState.suggestion}
          accounts={accountOptions}
          prefillSourceIds={ruleState.prefillSourceIds}
          saving={savingRule}
          onConfirm={confirmRule}
          onDismiss={() => setRuleState(null)}
        />
      )}

      {createRuleFor && (
        <CreateRuleDialog
          description={createRuleFor.description}
          stakeholders={allStakeholders}
          accounts={accountOptions}
          natures={data.expenseNatures}
          onClose={() => setCreateRuleFor(null)}
          onApplied={(message) => {
            setCreateRuleFor(null);
            refreshInPlace();
            window.alert(message);
          }}
        />
      )}

      {txnsDialogStakeholder && (
        <StakeholderTxnsDialog
          stakeholder={txnsDialogStakeholder}
          perspectiveId={getPerspectiveId() ?? null}
          onClose={() => setTxnsDialogStakeholder(null)}
          onChanged={refreshInPlace}
        />
      )}
    </>
  );
}
